import {
  CustomMappingType,
  ICustomMapping,
  ICustomMappingSourceOption,
  IFailedCustomMappingRecord,
  IPracticeMigration,
  ITranslationMap,
} from '@principle-theorem/practice-migrations/interfaces';
import { ManualTransactionType } from '@principle-theorem/principle-core';
import {
  IManualTransactionType,
  TransactionProvider,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  IBlobFilenamePair,
  INamedDocument,
  WithPrimaryKey,
  XSLXImporterExporter,
  asyncForEach,
  multiMap,
  multiSortBy$,
  nameSorter,
  snapshot,
  type WithRef,
} from '@principle-theorem/shared';
import { compact, first, sortBy, uniqBy } from 'lodash';
import { scan } from 'rxjs/operators';
import Fuse from 'fuse.js';
import { BaseCustomMappingHandler } from '../../../base-custom-mapping-handler';
import { CustomMapping } from '../../../custom-mapping';
import { PracticeMigration } from '../../../practice-migrations';
import { TranslationMapHandler } from '../../../translation-map';
import {
  AgreementHoldersV2SourceEntity,
  IExactV2AgreementHolder,
} from '../../source/entities/agreement-holders-v2';
import { PayorLabelToXSLX } from './payor-label-to-xlsx';
import { XSLXToPayorLabel } from './xlsx-to-payor-label';

export const PAYOR_LABEL_CUSTOM_MAPPING_TYPE = 'exactPayorLabelMapping';

export const PAYOR_LABEL_MAPPING: ICustomMapping = CustomMapping.init({
  metadata: {
    label: 'Payor Labels',
    description: `
      Maps Exact agreement-funded payors (identified via trans4.payorid) to a
      Principle Manual Transaction Type.

      Agreement-funded transactions reference a payor via the payorid column.
      Source options are auto-detected from the practice database — localpayor
      scheme codes and debtor accounts used as agreement holders are both included.

      All agreement-funded payors are mapped to TransactionProvider.Manual; the
      destination dropdown selects which per-brand Manual Transaction Type to use.
    `,
    type: PAYOR_LABEL_CUSTOM_MAPPING_TYPE,
  },
  type: CustomMappingType.DocumentReference,
  labelOverrides: {
    sourceIdentifier: 'Payor ID',
    sourceLabel: 'Payor Name',
    destinationIdentifier: 'Manual Transaction Type',
  },
});

export class ExactPayorLabelMappingHandler extends BaseCustomMappingHandler<
  IManualTransactionType,
  string
> {
  customMapping = PAYOR_LABEL_MAPPING;

  async getSourceOptions(
    migration: WithRef<IPracticeMigration>
  ): Promise<ICustomMappingSourceOption[]> {
    const sourceEntity = new AgreementHoldersV2SourceEntity();
    const records = await sourceEntity
      .getAllRecords$(migration)
      .pipe(
        multiMap((record) => record.data),
        scan(
          (all, batch) => [...all, ...batch],
          [] as IExactV2AgreementHolder[]
        )
      )
      .toPromise();

    const options = uniqBy(
      (records ?? []).map((record) => ({
        label: sourceEntity.getSourceLabel(record),
        value: record.id,
      })),
      (option) => option.value
    );

    return sortBy(options, 'label');
  }

  async getDestinationOptions(
    migration: WithRef<IPracticeMigration>
  ): Promise<INamedDocument<IManualTransactionType>[]> {
    return snapshot(
      ManualTransactionType.all$(migration.configuration.brand).pipe(
        multiSortBy$(nameSorter()),
        multiMap((type) => ({ name: type.name, ref: type.ref })),
        multiSortBy$(nameSorter())
      )
    );
  }

  async autocompleteMappings(
    migration: WithRef<IPracticeMigration>
  ): Promise<void> {
    const translationMap = new TranslationMapHandler(migration);
    const sourceOptions = await this.getSourceOptions(migration);
    const destinationOptions = await this.getDestinationOptions(migration);
    const existingRecords = await this.getRecords(translationMap);

    const alreadyMappedIdentifiers = existingRecords
      .filter((existingRecord) => !!existingRecord.destinationIdentifier)
      .map((existingRecord) => existingRecord.sourceIdentifier);

    const unmappedSourceOptions = sourceOptions.filter(
      (sourceOption) => !alreadyMappedIdentifiers.includes(sourceOption.value)
    );

    const fuzzyMatcher = new Fuse(destinationOptions, {
      keys: ['name'],
      includeScore: true,
      threshold: 0.3,
    });

    const recordsForUpload = unmappedSourceOptions.map((sourceOption) => {
      const matches = fuzzyMatcher.search(sourceOption.label);
      const closestMatch = first(matches)?.item;

      if (!closestMatch) {
        return;
      }

      return {
        destinationIdentifier: closestMatch.ref,
        destinationValue: { value: closestMatch.name },
        sourceIdentifier: sourceOption.value,
        sourceLabel: sourceOption.label,
      };
    });

    await this.upsertBulk(compact(recordsForUpload), translationMap);
  }

  async getMappingBlob(
    migration: WithRef<IPracticeMigration>
  ): Promise<IBlobFilenamePair> {
    const { fileName, payorLabels, translators } =
      await this._getExporterData(migration);

    return new XSLXImporterExporter().getBlob(
      fileName,
      payorLabels,
      translators
    );
  }

  async downloadMapping(migration: WithRef<IPracticeMigration>): Promise<void> {
    const { fileName, payorLabels, translators } =
      await this._getExporterData(migration);

    await new XSLXImporterExporter().download(
      fileName,
      payorLabels,
      translators
    );
  }

  async uploadMapping(
    migration: WithRef<IPracticeMigration>,
    file: File
  ): Promise<void> {
    const items = await new XSLXImporterExporter().parse(
      file,
      new XSLXToPayorLabel()
    );

    const translationMap = new TranslationMapHandler(migration);
    const records = await this.getRecords(translationMap);
    await asyncForEach(records, (record) =>
      translationMap.deleteRecord(record)
    );

    const sourceOptions = await this.getSourceOptions(migration);
    const destinationOptions = await this.getDestinationOptions(migration);

    const failedRecords: IFailedCustomMappingRecord[] = [];

    const recordsForUpload = items.map((item) => {
      const value = sourceOptions.find(
        (sourceOption) => sourceOption.value === item.sourceId
      )?.value;
      if (!value || !item.mapTo) {
        return;
      }

      const manualType = destinationOptions.find(
        (option) => option.name === item.mapTo
      );

      if (!manualType) {
        failedRecords.push({
          sourceIdentifier: value,
          sourceLabel: item.sourceName,
          reason: `Destination identifier "${item.mapTo?.trim()}" not found`,
        });
        return;
      }

      return {
        destinationIdentifier: manualType.ref,
        destinationValue: { value: manualType.name },
        sourceIdentifier: value,
        sourceLabel: item.sourceName,
      };
    });

    await this.upsertBulk(compact(recordsForUpload), translationMap);

    const customMapping = await snapshot(
      PracticeMigration.findCustomMapping$(migration, this.customMapping)
    );
    if (!customMapping) {
      throw new Error('Custom mapping not found for updating failed records');
    }
    await Firestore.patchDoc(customMapping.ref, { failedRecords });
  }

  private async _getExporterData(
    migration: WithRef<IPracticeMigration>
  ): Promise<{
    fileName: string;
    payorLabels: ICustomMappingSourceOption[];
    translators: PayorLabelToXSLX;
  }> {
    const fileName = this.getFileName();
    const payorLabels = await this.getSourceOptions(migration);
    const translationMap = new TranslationMapHandler(migration);
    const translators = new PayorLabelToXSLX(
      await this.getDestinationOptions(migration),
      await this.getRecords(translationMap)
    );
    return { fileName, payorLabels, translators };
  }
}

export function resolvePayorTransactionType(
  mappings: WithPrimaryKey<ITranslationMap<IManualTransactionType, string>>[],
  payorId?: string
):
  | {
      provider: TransactionProvider;
      manualType?: INamedDocument<IManualTransactionType>;
    }
  | undefined {
  if (!payorId) {
    return;
  }
  const mapping = mappings.find(
    (record) => record.sourceIdentifier === payorId
  );
  if (!mapping?.destinationValue?.value || !mapping.destinationIdentifier) {
    return;
  }

  return {
    provider: TransactionProvider.Manual,
    manualType: {
      name: mapping.destinationValue.value,
      ref: mapping.destinationIdentifier,
    },
  };
}
